import { Conversation } from "../api/models/Conversation";
import { Message } from "../api/models/Message";
import { useConversationStore } from "../store/conversation-store";

type SocketEvent = {
  type: string;
  data?: {
    metadata?: Record<string, string>;
    function_call_details?: { name?: string; args?: unknown };
    message?: string;
  };
};

export interface ChatTransportOptions {
  accessToken: string;
  getConversation: (conversationId: string, accessToken: string) => Promise<Conversation>;
  refreshConversationList: () => Promise<void>;
  onFunctionCall: (name: string, args: unknown) => void;
  onWorkflowUpdate: (message?: string) => void;
  onFreeCreditUsed: () => void;
  onReconcileError: () => void;
}

const wsUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || "";
  return apiUrl.replace(/^http:/, "ws:").replace(/^https:/, "wss:") + "/v1/events";
};

/**
 * A single owner for the chat connection. It deliberately has no React state:
 * React Strict Mode can mount/unmount effects twice, while this owner uses a
 * generation token to make every old socket and reconnect callback inert.
 */
export class ChatTransport {
  private socket?: WebSocket;
  private options?: ChatTransportOptions;
  private generation = 0;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private reconciliations = new Map<string, ReturnType<typeof setTimeout>>();
  private responseWatchdogs = new Map<string, ReturnType<typeof setTimeout>>();
  private reconnectAttempt = 0;

  start(options: ChatTransportOptions) {
    this.stop();
    this.options = options;
    const generation = this.generation;
    this.connect(generation);
  }

  stop() {
    this.generation += 1;
    this.reconnectAttempt = 0;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = undefined;
    this.reconciliations.forEach(clearTimeout);
    this.reconciliations.clear();
    this.responseWatchdogs.forEach(clearTimeout);
    this.responseWatchdogs.clear();
    const socket = this.socket;
    this.socket = undefined;
    if (socket && socket.readyState < WebSocket.CLOSING) socket.close(1000, "transport stopped");
  }

  /**
   * POST confirmation means a response exists even if the socket is replaced
   * before its first event arrives. This bounded, low-frequency fallback is
   * deliberately per response (not a polling loop for the whole page).
   */
  expectResponse(conversationId: string) {
    if (this.responseWatchdogs.has(conversationId)) return;
    this.responseWatchdogs.set(conversationId, setTimeout(() => void this.recoverExpectedResponse(conversationId, 0), 10_000));
  }

  private connect(generation: number) {
    if (generation !== this.generation || !this.options) return;
    const socket = new WebSocket(wsUrl());
    this.socket = socket;
    socket.onopen = () => {
      if (generation !== this.generation || this.socket !== socket || !this.options) return;
      this.reconnectAttempt = 0;
      socket.send(JSON.stringify({ type: "authorization", data: this.options.accessToken }));
      // The server currently uses an application-level heartbeat. Send a pong
      // proactively as well as replying to pings: a dev-server reload or a
      // briefly busy browser must not leave an otherwise-open stream orphaned.
      this.sendPong(socket);
      this.heartbeatTimer = setInterval(() => this.sendPong(socket), 5_000);
    };
    socket.onmessage = (event) => {
      if (generation !== this.generation || this.socket !== socket) return;
      this.handleMessage(event.data);
    };
    socket.onclose = () => {
      if (generation !== this.generation || this.socket !== socket) return;
      if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
      this.scheduleReconnect(generation);
    };
    socket.onerror = () => {
      // onclose schedules the bounded reconnect. Logging every transient error
      // created noisy dev-mode consoles without giving the user an action.
    };
  }

  private scheduleReconnect(generation: number) {
    if (generation !== this.generation) return;
    const delay = Math.min(10_000, 500 * 2 ** this.reconnectAttempt++);
    this.reconnectTimer = setTimeout(() => this.connect(generation), delay + Math.round(Math.random() * 250));
  }

  private handleMessage(rawMessage: unknown) {
    let event: SocketEvent;
    try {
      event = JSON.parse(String(rawMessage));
    } catch {
      return;
    }
    if (event.type === "ping") {
      this.sendPong(this.socket);
      return;
    }

    const metadata = event.data?.metadata || {};
    const conversationId = metadata.conversation_id;
    const store = useConversationStore.getState();
    switch (event.type) {
      case "response_created":
        if (conversationId) store.beginResponse(conversationId);
        break;
      case "message_delta":
        if (conversationId) store.applyMessageDelta(conversationId, Number(metadata.sequence_number), metadata.delta || "");
        break;
      case "reasoning_delta":
        if (conversationId) store.applyReasoningDelta(conversationId, Number(metadata.sequence_number), metadata.delta || "");
        break;
      case "message_completed":
      case "run_completed":
      case "conversation_evicted_from_cache":
        if (conversationId) this.scheduleReconciliation(conversationId);
        break;
      case "run_failed":
        if (conversationId) {
          this.clearResponseWatchdog(conversationId);
          useConversationStore.getState().failStream(conversationId);
        }
        this.options?.onReconcileError();
        break;
      case "function_call":
        this.options?.onFunctionCall(event.data?.function_call_details?.name || "", event.data?.function_call_details?.args);
        break;
      case "workflow_update":
        this.options?.onWorkflowUpdate(event.data?.message);
        break;
      case "free_credit_used":
        this.options?.onFreeCreditUsed();
        break;
    }
  }

  private sendPong(socket?: WebSocket) {
    if (socket?.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify({ type: "pong" }));
    } catch {
      // The close handler owns reconnects; a send during teardown is harmless.
    }
  }

  /** Collapse completion/eviction duplicates into one read, then retry the
   * occasional cache/store race with a small bounded backoff. */
  private scheduleReconciliation(conversationId: string) {
    if (this.reconciliations.has(conversationId)) return;
    this.reconciliations.set(conversationId, setTimeout(() => {
      this.reconciliations.delete(conversationId);
      void this.reconcile(conversationId, 0);
    }, 350));
  }

  private async reconcile(conversationId: string, attempt: number): Promise<void> {
    const options = this.options;
    if (!options) return;
    try {
      const conversation = await options.getConversation(conversationId, options.accessToken);
      const local = useConversationStore.getState().currentConversation;
      const localMessageCount = (local?.id === conversationId ? local.messages : [])?.filter((message) => message.id !== "in-flight-message").length || 0;
      const serverMessages = conversation.messages || [];
      const serverLastMessage = serverMessages[serverMessages.length - 1];
      // A cache read can legitimately return before the worker has written the
      // final assistant message. Retrying is safer than replacing rich local
      // content with that older snapshot.
      if (serverMessages.length <= localMessageCount || serverLastMessage?.role !== Message.RoleEnum.Assistant) {
        throw new Error("conversation snapshot is not terminal yet");
      }
      useConversationStore.getState().completeStream(conversationId, conversation);
      this.clearResponseWatchdog(conversationId);
      await options.refreshConversationList();
    } catch {
      if (attempt < 3) {
        setTimeout(() => void this.reconcile(conversationId, attempt + 1), 400 * 2 ** attempt);
      } else {
        // Never leave the composer disabled if the terminal snapshot cannot
        // be read (for example after a backend restart or a failed run).
        useConversationStore.getState().failStream(conversationId);
        this.clearResponseWatchdog(conversationId);
        options.onReconcileError();
      }
    }
  }

  private async recoverExpectedResponse(conversationId: string, attempt: number): Promise<void> {
    const options = this.options;
    if (!options || !this.responseWatchdogs.has(conversationId)) return;
    try {
      const conversation = await options.getConversation(conversationId, options.accessToken);
      const local = useConversationStore.getState().currentConversation;
      const localMessageCount = (local?.id === conversationId ? local.messages : [])?.filter((message) => message.id !== "in-flight-message").length || 0;
      const messages = conversation.messages || [];
      if (messages.length > localMessageCount && messages[messages.length - 1]?.role === Message.RoleEnum.Assistant) {
        useConversationStore.getState().completeStream(conversationId, conversation);
        this.clearResponseWatchdog(conversationId);
        await options.refreshConversationList();
        return;
      }
    } catch {
      // A backend restart is recoverable. Try again below rather than treating
      // a single failed read as a failed model response.
    }

    if (!this.responseWatchdogs.has(conversationId)) return;

    if (attempt < 5) {
      this.responseWatchdogs.set(conversationId, setTimeout(() => void this.recoverExpectedResponse(conversationId, attempt + 1), 15_000));
      return;
    }
    this.clearResponseWatchdog(conversationId);
    useConversationStore.getState().failStream(conversationId);
    options.onReconcileError();
  }

  private clearResponseWatchdog(conversationId: string) {
    const timer = this.responseWatchdogs.get(conversationId);
    if (timer) clearTimeout(timer);
    this.responseWatchdogs.delete(conversationId);
  }
}
